/**
 * @author: Rakesh Seervi
 * @date : 10-Jun-2025
 * File Description: Tech specification Modal handle add and edit product tech specification UI and API wiring up
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { debounce } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { fetchSingleTechSPecification, fetchTechSpecification } from "middleware/services/cmsApi";
import { techSpecificationSchema } from "app/helper/validationSchema/productValidationSchema";
import { TextField, SelectedUnitDropdown, TechAutoCompleteDropdown } from "app/components/CustomFormElements/syncFusionFormFields";
import { saveProductSpecification, updateProductSpecification } from "middleware/services/productApi";
import { actionError, actionSuccess, hideLoader, showLoader } from "middleware/actions/utilityAction";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { setBreadCrumb } from "middleware/actions/breadCrumbAction";

// default state
const initialState = {
  specificationName: "",
  specificationUnits: [],
  specificationUnit: "",
  specificationValueFormat: "",
  specificationValueType: "",
  singleValue: "",
  minValue: 0,
  maxValue: 0,
};

const TechnicalSpecsModal = (props) => {
  const { companyId, productId } = props.computedMatch?.params || {};
  const { accessRights: aR } = props;
  const { item } = props.location.state || {};
  const { editData } = props;
  const [options, setOptions] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [productName, setProductName] = useState("");
  const { productDetails } = useSelector((state) => state.productState || {});
  const split_url = props.location.pathname.split("/");
  const backPath = editData ? `${split_url.slice(0, -3).join("/")}/edit#overview` : `${split_url.slice(0, -2).join("/")}/edit#overview`;
  const dispatch = useDispatch();
  const history = useHistory();

  const isFirstRender = useRef(true);

  const formik = useFormik({
    initialValues: {
      ...initialState,
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: () =>
      Yup.lazy((values) => {
        if (values?.specificationValueType == "NUMERIC") {
          if (typeof techSpecificationSchema.minValue == "undefined") {
            techSpecificationSchema.minValue = Yup.number().required("Please enter parameter min value");
            techSpecificationSchema.maxValue = Yup.number()
              .required("Please enter parameter max value")
              .test("max-greater-than-min", "Max value should be greater than or equal to min value", function (value) {
                const { minValue } = this.parent;
                return typeof minValue === "undefined" || typeof value === "undefined" || value >= minValue;
              });
          }
          delete techSpecificationSchema.singleValue;
        } else {
          delete techSpecificationSchema.minValue;
          delete techSpecificationSchema.maxValue;
        }
        return Yup.object().shape(techSpecificationSchema);
      }),
  });

  const breadCrumbLinks = useMemo(() => {
    const links = [{ linkUrl: "/company", linkName: `Company`, linkActive: false }];
    if (companyId && companyName) {
      links.push({ linkUrl: `/company/${companyId}/edit#basic`, linkName: companyName, linkActive: false });
      links.push({ linkUrl: `/company/${companyId}/edit#basic`, linkName: aR.moduleName, linkActive: false });
    }

    if (productId && productName) {
      links.push({
        linkUrl: `/company/${companyId}/product/${productId}/edit#overview`,
        linkName: productName,
        linkActive: false,
      });
      links.push({
        linkUrl: `/company/${companyId}/product/${productId}/edit#overview`,
        linkName: "Technical Specification",
        linkActive: false,
      });
      links.push({ linkUrl: ``, linkName: props.editData ? "Edit" : "Add New", linkActive: true });
    }

    return links;
  }, [companyId, companyName, productId, productName]);

  const fetchUnitofMeasurementValue = (specificationId) => {
    fetchSingleTechSPecification(specificationId).then((res) => {
      const specificationUnits = res.data.data?.specificationUnit.map((item) => {
        return { label: item.unitName, value: item.unitId };
      });
      if (specificationUnits?.length) formik.setFieldValue("specificationUnits", specificationUnits);
    });
  };
  const setFormikValues = () => {
    fetchUnitofMeasurementValue(item.specificationId);
    const specificationValueType = item?.specificationValue?.max || item?.specificationValue?.max === 0 ? "NUMERIC" : "ALPHA";
    const value =
      specificationValueType == "NUMERIC"
        ? {
            minValue: item?.specificationValue?.min,
            maxValue: item?.specificationValue?.max,
          }
        : { singleValue: item?.specificationValue?.single };
    const formikData = {
      id: item?.id,
      specificationId: item?.specificationId,
      specificationName: item?.specificationName,
      specificationUnit: item?.specificationUnit,
      specificationValueType: specificationValueType,
      ...value,
    };
    setOptions({ label: item?.specificationName, value: item?.specificationId });

    formik.setValues(formikData);
  };

  useEffect(() => {
    if (editData) {
      setFormikValues();
    }
  }, [editData]);

  useEffect(() => {
    if (formik.values?.specificationUnits) {
      let val = formik.values.specificationUnits.find((s) => s.label === formik.values.specificationUnit);
      val && formik.setFieldValue("specificationUnit", val.label);
    }
  }, [formik.values?.specificationUnits]);

  const saveSpecification = async (data) => {
    const specificationValue = data?.specificationValueType == "NUMERIC" ? { min: data.minValue, max: data.maxValue } : { single: data.singleValue };
    const payload = {
      specificationId: data.specificationId,
      specificationName: data.specificationName,
      specificationUnit: data?.specificationUnit,
      specificationValue: specificationValue,
    };

    try {
      dispatch(showLoader());
      await saveProductSpecification(productId, payload);
      dispatch(hideLoader());
      dispatch(actionSuccess("Technical specification saved successfully."));
      formik.handleReset();
      history.push(backPath, { currentIndex: 1 });
    } catch (err) {
      dispatch(actionError(err.data?.message || "Something went wrong."));
    } finally {
      dispatch(hideLoader());
    }
  };

  const updateSpecification = async (data) => {
    const specificationValue =
      formik.values?.specificationValueType == "NUMERIC"
        ? { min: (data.singleValue && data.singleValue.toString()) || data.minValue, max: data.maxValue }
        : { single: data.singleValue.toString() };
    const id = data.id;
    const payload = {
      specificationId: data.specificationId,
      specificationName: data.specificationName,
      specificationUnit: data.specificationUnit,
      specificationValue,
    };
    try {
      dispatch(showLoader());
      await updateProductSpecification(productId, id, payload);
      dispatch(hideLoader());
      dispatch(actionSuccess("Technical specification updated successfully."));
      formik.handleReset();
      history.push(backPath, { currentIndex: 1 });
    } catch (err) {
      dispatch(actionError(err.data?.message || "Something went wrong."));
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleSave = async () => {
    formik.validateForm().then((res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).forEach((field) => {
          touched[field] = true;
        });
        formik.setFormikState({ ...formik, touched: touched, errors: res });
      } else {
        editData ? updateSpecification(formik.values) : saveSpecification(formik.values);
      }
    });
  };

  const searchText = useCallback(
    debounce(async (e) => {
      const params = { filters: [["specificationName", "cn", e.text]] };
      const response = await fetchTechSpecification(params);
      if (response?.data?.data) {
        setOptions(response.data.data);
        const resData = response.data.data.map((v) => {
          return {
            value: v.id,
            label: v.specificationName,
          };
        });
        if (resData) {
          e.updateData(resData);
        } else {
          e.updateData([]);
        }
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (Object.keys(productDetails).length > 0) {
      setProductName(productDetails.productName);
      setCompanyName(productDetails.productCompany.companyName);
    }
    dispatch(setBreadCrumb(breadCrumbLinks));
  }, [dispatch, productDetails, breadCrumbLinks]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!formik.values?.specificationName) {
      formik.setFieldValue("specificationUnits", []);
      formik.setFieldValue("specificationUnit", "");
      formik.setFieldValue("minValue", 0);
      formik.setFieldValue("maxValue", 0);
      formik.setFieldValue("singleValue", "");
    }
  }, [formik.values?.specificationName]);

  return (
    <div className="p-0 mx-3">
      <div className="p-2 border custom-bg-grey text-white rounded-top">Products &amp; Technology - Technical Specification</div>
      <div className="d-flex flex-column justify-content-between border border-top-0 custom-specification-container">
        <div className="specification-row">
          <div className="specification-row__name-field">
            <TechAutoCompleteDropdown
              formik={formik}
              placeholder="Parameter Name *"
              id="specificationName"
              name="specificationName"
              select={(selectedOption) => {
                if (selectedOption?.itemData?.value && options.length > 0) {
                  let filteredvalues = options.filter((item) => item.id == selectedOption?.itemData?.value);
                  const specificationUnits = filteredvalues[0]?.specificationUnit.map((item) => {
                    return { label: item.unitName, value: item.unitId };
                  });

                  formik.setFieldValue("specificationId", selectedOption?.itemData.value);
                  formik.setFieldValue("specificationName", selectedOption.itemData);
                  formik.setFieldValue("specificationUnits", specificationUnits);
                  formik.setFieldValue("specificationUnit", "");
                  formik.setFieldValue("minValue", 0);
                  formik.setFieldValue("maxValue", 0);
                  formik.setFieldValue("singleValue", "");
                  formik.setFieldValue("specificationValueFormat", filteredvalues[0].specificationValueFormat);
                  formik.setFieldValue("specificationValueType", filteredvalues[0]?.specificationValueType);
                } else {
                  formik.setFieldValue("specificationUnit", "");
                  formik.setFieldValue("specificationName", "");
                  formik.setFieldValue("specificationUnits", []);
                  formik.setFieldValue("minValue", 0);
                  formik.setFieldValue("maxValue", 0);
                  formik.setFieldValue("singleValue", "");
                }
              }}
              allowFiltering={true}
              filtering={searchText}
              autofill={true}
              fields={{ value: "label", id: "value" }}
            />
          </div>
          <div className="specification-row__unit-field">
            <SelectedUnitDropdown
              formik={formik}
              id="specificationUnit"
              name="specificationUnit"
              placeholder="Unit of Measurement *"
              dataSource={formik.values?.specificationUnits || []}
              filtering={true}
              fields={{ text: "label", value: "value" }}
            />
          </div>

          <div className="specification-row__value-fields" style={{ display: formik.values?.specificationValueType === "NUMERIC" ? "flex" : "none" }}>
            <div className="specification-row__min-value">
              <TextField formik={formik} type="number" name="minValue" id="minValue" placeholder="Parameter Min Value *" />
            </div>
            <div className="specification-row__max-value">
              <TextField formik={formik} type="number" name="maxValue" id="maxValue" placeholder="Max Value *" />
            </div>
          </div>

          <div
            className="specification-row__value-fields1"
            style={{ display: formik.values?.specificationValueType !== "NUMERIC" ? "block" : "none" }}
          >
            <div className="specification-row__single-value">
              <TextField formik={formik} name="singleValue" id="singleValue" className="form-control" placeholder="Parameter Value *" />
            </div>
          </div>
        </div>

        <div className="text-right border-top p-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              formik.handleReset();
              history.push(backPath, { currentIndex: 1 });
            }}
            className="btn btn-outline-dark mr-3 company-button"
          >
            Cancel
          </button>
          <button type="button" className="btn btn-primary company-button" onClick={handleSave} disabled={false}>
            {editData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
TechnicalSpecsModal.propTypes = {
  editData: PropTypes.bool,
};
export default TechnicalSpecsModal;
